import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, MapPin, Expand } from 'lucide-react';
import { ImageLightbox } from '../components/ImageLightbox';
import { boathouse, boathousePhotos } from '../data/boathouse';
const ease = [0.23, 1, 0.32, 1] as const;
export function BoathousePage() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [lead, ...rest] = boathousePhotos;
  return <div className="bg-white" data-id="element-309" data-mp="1iyj1n2yf2cjh">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden bg-black" data-id="element-310" data-mp="1ix7vwxleuupq">
        <img src={boathouse.heroImage} alt="Concept rendering of the Boathouse tiki bar and grill on the water at sunset" className="absolute inset-0 w-full h-full object-cover" data-id="element-311" data-mp="14xq1a1lfon8g" />
        <div className="absolute inset-0 bg-black/50" aria-hidden="true" data-id="element-312" data-mp="3pqjv87154p6l" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-16 md:pb-20" data-id="element-313" data-mp="3up4vg6ge78v1">
          <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-[#C5A55A] transition-colors mb-10" data-id="element-314" data-mp="acbf4xlaczch">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" data-id="element-315" data-mp="kb0mvxjgx3f0" />
            All projects
          </Link>
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease
        }} data-id="element-316" data-mp="f9uxa8l3tw9p">
            <p className="text-[#C5A55A] text-xs md:text-sm tracking-[0.3em] uppercase mb-5" data-id="element-317" data-mp="p9lpu4q3u175">
              {boathouse.status}
            </p>
            <h1 className="font-serif font-bold text-white leading-[0.95]" data-id="element-318" data-mp="12zyy686wrhx0">
              <span className="block text-5xl md:text-7xl lg:text-8xl" data-id="element-319" data-mp="y2f727p43ann">
                {boathouse.name}
              </span>
              <span className="block text-2xl md:text-4xl italic font-normal text-[#C5A55A] mt-3" data-id="element-320" data-mp="1cywi0xte2n9o">
                {boathouse.subtitle}
              </span>
            </h1>
            <p className="flex items-center gap-2 text-gray-200 mt-8" data-id="element-321" data-mp="1f3t3b6f9jsts">
              <MapPin className="w-4 h-4 text-[#C5A55A] shrink-0" aria-hidden="true" data-id="element-322" data-mp="1k1l64vhg4zcr" />
              {boathouse.location}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 px-6 md:px-10" data-id="element-323" data-mp="1ts6025m6dfpc">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20" data-id="element-324" data-mp="1tsz5925hjx5d">
          <div className="lg:col-span-7" data-id="element-325" data-mp="1yqhektnf0edw">
            <p className="text-2xl md:text-4xl font-serif text-black leading-snug mb-8" data-id="element-326" data-mp="22s2p48ch8gkx">
              {boathouse.tagline}
            </p>
            <div className="w-16 h-[2px] bg-[#C5A55A] mb-8" data-id="element-327" data-mp="27sqjn3zwzfjw" />
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl" data-id="element-328" data-mp="2cr1xrqx8sc7x">
              {boathouse.intro}
            </p>
          </div>
          <div className="lg:col-span-5" data-id="element-329" data-mp="dez3rmqzrzg0">
            <h2 className="text-xs tracking-[0.25em] uppercase text-gray-500 mb-4" data-id="element-330" data-mp="ichijtrox8vx">
              What's planned
            </h2>
            <ul className="border-t border-gray-200" data-id="element-331" data-mp="at84hn88om0j">
              {boathouse.features.map(f => <li key={f} className="py-4 border-b border-gray-200 text-black" data-id="element-332" data-mp="618bkeql6tdg">
                  {f}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Renderings */}
      <section className="bg-[#0A0A0A] text-white py-24 px-6 md:px-10" data-id="element-333" data-mp="3sb12swlet5ll">
        <div className="max-w-7xl mx-auto" data-id="element-334" data-mp="tqsvazukduns">
          <p className="text-[#C5A55A] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-335" data-mp="tprh4mi94s1u">
            Renderings
          </p>
          <h2 className="text-3xl md:text-5xl font-serif font-bold mb-12" data-id="element-336" data-mp="jtoap4gda793">
            See the Boathouse
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-337" data-mp="evcxqkurzgmn">
            <GalleryTile photo={lead} onOpen={() => setLightboxIndex(0)} className="md:col-span-3" imgClass="h-72 md:h-[540px]" data-id="element-338" data-mp="ewxvskgathp9" />
            {rest.map((photo, i) => <GalleryTile key={photo.src} photo={photo} onOpen={() => setLightboxIndex(i + 1)} imgClass="h-60 md:h-72" data-id="element-339" data-mp="s9c15vautii8" data-mpi={i} />)}
          </div>

          <p className="text-gray-500 text-sm mt-8" data-id="element-340" data-mp="icz8n0r1pap2">
            Concept renderings for illustration only; the final design is
            subject to change.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-10" data-id="element-341" data-mp="1ay0oenwt7zx6">
        <div className="max-w-5xl mx-auto text-center" data-id="element-342" data-mp="1fzg400g7xe86">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-black mb-6" data-id="element-343" data-mp="1fwtzk9omjeu6">
            Already a Boat House regular?
          </h2>
          <p className="text-gray-600 text-lg mb-10 max-w-2xl mx-auto" data-id="element-344" data-mp="1607dxioh4jr3">
            See menus, live music and current locations on the Boat House
            website — or talk with Sinclair about the development.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-345" data-mp="3qssrhm8umy9a">
            <a href={boathouse.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-black hover:bg-[#C5A55A] text-white hover:text-black px-8 py-4 font-medium rounded-sm transition-colors duration-200" data-id="element-346" data-mp="28sb9pdsa43b7">
              Visit theboathouseusa.com
              <ArrowUpRight className="w-5 h-5" aria-hidden="true" data-id="element-347" data-mp="1q2v8eo8rs6tq" />
            </a>
            <Link to="/contact" className="inline-flex items-center bg-[#C5A55A] hover:bg-[#D4AF37] text-black px-8 py-4 font-medium rounded-sm transition-colors duration-200" data-id="element-348" data-mp="1q0qzfrw0khml">
              Contact Sinclair
            </Link>
          </div>
        </div>
      </section>

      <ImageLightbox images={boathousePhotos} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={setLightboxIndex} data-id="element-349" data-mp="16evx9caamxns" />
    </div>;
}
interface GalleryTileProps {
  photo: {
    src: string;
    title: string;
  };
  onOpen: () => void;
  className?: string;
  imgClass: string;
}
function GalleryTile({
  photo,
  onOpen,
  className = '',
  imgClass
}: GalleryTileProps) {
  return <button onClick={onOpen} className={`group relative overflow-hidden text-left ${className}`} aria-label={`Open ${photo.title}`} data-id="element-350" data-mp="3b7u12lwds991">
      <img src={photo.src} alt={photo.title} loading="lazy" className={`w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02] ${imgClass}`} data-id="element-351" data-mp="3g918gjum36f6" />
      <span className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 px-5 py-4 bg-black/60" data-id="element-352" data-mp="2shm28fzps4yn">
        <span className="font-serif text-lg" data-id="element-353" data-mp="2xh8fuqjgfn7z">{photo.title}</span>
        <Expand className="w-4 h-4 text-[#C5A55A] shrink-0 opacity-70 group-hover:opacity-100 transition-opacity duration-200" aria-hidden="true" data-id="element-354" data-mp="2xecfywo1qogw" />
      </span>
    </button>;
}